import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Hash, Info, Search, RotateCcw, CheckCircle, XCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const PrimeNumberChecker: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<{ isPrime: boolean; factors?: number[]; message: string } | null>(null);
  const [primeRange, setPrimeRange] = useState({ min: 1, max: 100 });
  const [primeList, setPrimeList] = useState<number[]>([]);
  const [history, setHistory] = useState<Array<{ number: number; isPrime: boolean; time: string }>>([]);

  useSEO({
    title: 'Prime Number Checker - Check if a Number is Prime',
    description: 'Free online prime number checker. Instantly check if any number is prime, find factors, and generate lists of prime numbers.',
    keywords: 'prime number checker, prime numbers, factors, composite numbers, mathematics tool',
    canonical: 'https://snap-tools.online/calc/prime-number-checker'
  });

  const isPrime = (n: number): boolean => {
    if (n < 2) return false;
    if (n === 2) return true;
    if (n % 2 === 0) return false;
    
    for (let i = 3; i <= Math.sqrt(n); i += 2) {
      if (n % i === 0) return false;
    }
    return true;
  };

  const getFactors = (n: number): number[] => {
    const factors: number[] = [];
    for (let i = 1; i <= Math.sqrt(n); i++) {
      if (n % i === 0) {
        factors.push(i);
        if (i !== n / i) {
          factors.push(n / i);
        }
      }
    }
    return factors.sort((a, b) => a - b);
  };

  const checkNumber = (num: number) => {
    const prime = isPrime(num);
    const factors = prime ? [1, num] : getFactors(num);
    
    let message = '';
    if (num < 2) {
      message = `${num} is not considered a prime number (primes must be ≥ 2)`;
    } else if (prime) {
      message = `${num} is a prime number! It's only divisible by 1 and itself.`;
    } else {
      message = `${num} is a composite number. It has ${factors.length} factors.`;
    }

    setResult({ isPrime: prime, factors, message });
    
    // Add to history
    const historyEntry = { number: num, isPrime: prime, time: new Date().toLocaleTimeString() };
    setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
  };

  const handleCheck = () => {
    const num = parseInt(input);
    if (isNaN(num)) {
      toast.error('Please enter a valid number');
      return;
    }
    if (num < 0) {
      toast.error('Please enter a positive number');
      return;
    }
    if (num > 1000000) {
      toast.error('Please enter a number less than 1,000,000');
      return;
    }
    checkNumber(num);
  };

  const generatePrimes = (min: number, max: number): number[] => {
    const primes: number[] = [];
    for (let i = Math.max(2, min); i <= max; i++) {
      if (isPrime(i)) {
        primes.push(i);
      }
    }
    return primes;
  };

  const handleGeneratePrimes = () => {
    if (primeRange.min < 1 || primeRange.max < 1) {
      toast.error('Please enter positive numbers');
      return;
    }
    if (primeRange.min > primeRange.max) {
      toast.error('Minimum must be less than or equal to maximum');
      return;
    }
    if (primeRange.max - primeRange.min > 10000) {
      toast.error('Range too large. Please use a range of 10,000 or less');
      return;
    }

    const primes = generatePrimes(primeRange.min, primeRange.max);
    setPrimeList(primes);
    toast.success(`Found ${primes.length} prime numbers in range`);
  };

  const clearHistory = () => {
    setHistory([]);
    toast.success('History cleared');
  };

  const useFromHistory = (num: number) => {
    setInput(num.toString());
    checkNumber(num);
  };

  useEffect(() => {
    if (input && !isNaN(parseInt(input))) {
      const num = parseInt(input);
      if (num >= 0 && num <= 1000000) {
        checkNumber(num);
      }
    } else {
      setResult(null);
    }
  }, [input]);

  const firstPrimes = [2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71, 73, 79, 83, 89, 97];

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Hash className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Prime Number Checker
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Check if any number is prime, find all factors of composite numbers, and generate 
            lists of prime numbers within any range.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Prime Checker */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6 mb-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Check Prime Number</h2>
              
              {/* Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter a number
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter a positive integer"
                    min="0"
                    max="1000000"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent text-lg"
                  />
                  <button
                    onClick={handleCheck}
                    className="px-6 py-3 bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white rounded-lg hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                  >
                    <Search className="h-4 w-4" />
                    Check
                  </button>
                </div>
              </div>

              {/* Result */}
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <div className={`p-4 rounded-lg border ${
                    result.isPrime 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-orange-50 border-orange-200'
                  }`}>
                    <div className="flex items-center gap-3 mb-3">
                      {result.isPrime ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : (
                        <XCircle className="h-6 w-6 text-orange-600" />
                      )}
                      <span className={`font-semibold text-lg ${
                        result.isPrime ? 'text-green-700' : 'text-orange-700'
                      }`}>
                        {result.isPrime ? 'Prime Number' : 'Composite Number'}
                      </span>
                    </div>
                    <p className={`text-sm mb-3 ${
                      result.isPrime ? 'text-green-700' : 'text-orange-700'
                    }`}>
                      {result.message}
                    </p>
                    {result.factors && result.factors.length > 2 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Factors:</p>
                        <div className="flex flex-wrap gap-1">
                          {result.factors.map((factor, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                            >
                              {factor}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">About Prime Numbers:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• A prime number is divisible only by 1 and itself</li>
                      <li>• The smallest prime number is 2 (the only even prime)</li>
                      <li>• Numbers less than 2 are not considered prime</li>
                      <li>• Composite numbers have more than two factors</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Prime Generator */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Generate Prime Numbers</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From
                  </label>
                  <input
                    type="number"
                    value={primeRange.min}
                    onChange={(e) => setPrimeRange(prev => ({ ...prev, min: parseInt(e.target.value) || 1 }))}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To
                  </label>
                  <input
                    type="number"
                    value={primeRange.max}
                    onChange={(e) => setPrimeRange(prev => ({ ...prev, max: parseInt(e.target.value) || 100 }))}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
              </div>

              <button
                onClick={handleGeneratePrimes}
                className="w-full mb-4 px-4 py-2 bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white rounded-lg hover:shadow-lg transition-all duration-200"
              >
                Generate Primes
              </button>

              {primeList.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Prime numbers from {primeRange.min} to {primeRange.max} ({primeList.length} found):
                  </p>
                  <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3">
                    <div className="flex flex-wrap gap-1">
                      {primeList.map((prime, index) => (
                        <button
                          key={index}
                          onClick={() => useFromHistory(prime)}
                          className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm transition-colors"
                        >
                          {prime}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* First 25 Primes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <h3 className="font-semibold text-gray-900 mb-4">First 25 Prime Numbers</h3>
              <div className="grid grid-cols-5 gap-2">
                {firstPrimes.map((prime) => (
                  <button
                    key={prime}
                    onClick={() => useFromHistory(prime)}
                    className="p-2 text-sm bg-purple-50 hover:bg-purple-100 text-purple-700 rounded transition-colors"
                  >
                    {prime}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Recent Checks</h3>
                {history.length > 0 && (
                  <button
                    onClick={clearHistory}
                    className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                    title="Clear history"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No checks yet</p>
                ) : (
                  history.map((entry, index) => (
                    <button
                      key={index}
                      onClick={() => useFromHistory(entry.number)}
                      className="w-full text-left p-2 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm">{entry.number}</span>
                        <div className="flex items-center gap-2">
                          {entry.isPrime ? (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">Prime</span>
                          ) : (
                            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">Composite</span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">{entry.time}</div>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default PrimeNumberChecker;
